import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App, Form, Input, Modal, Segmented, Typography } from "antd";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { KEY_PLATFORMS, usePlatforms } from "./hooks";

type Values = { storeCode: string; username: string; sellerKey?: string; signatureKey?: string };

/**
 * Connects a store of a marketplace without OAuth (Blibli): either a bind
 * request the owner approves in Seller Center, or the API Seller Key the owner
 * generated. Opened by ?connect=<platform> on the Channel page (useConnect).
 */
export function KeyConnectModal() {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const platform = params.get("connect") as ChannelPlatformName | null;
  const open = platform !== null && KEY_PLATFORMS.includes(platform);
  const bindId = usePlatforms().data?.items.find((p) => p.platform === platform)?.bindId;
  const [mode, setMode] = useState<"bind" | "key">("bind");
  const [form] = Form.useForm<Values>();
  const close = () => setParams({}, { replace: true });

  const submit = useMutation({
    mutationFn: async (v: Values) => {
      const path = { platform: platform as ChannelPlatformName };
      if (mode === "bind") {
        await unwrap(api.POST("/v1/channels/{platform}/bind", { params: { path }, body: { storeCode: v.storeCode, username: v.username } }));
      } else {
        await unwrap(
          api.POST("/v1/channels/{platform}/key", {
            params: { path },
            body: { storeCode: v.storeCode, username: v.username, sellerKey: v.sellerKey ?? "", signatureKey: v.signatureKey || undefined },
          }),
        );
      }
      return mode;
    },
    onSuccess: (done) => {
      void message.success(
        done === "bind" ? t("channels.key.bindSent") : t("channels.result.connected", { count: "1", platform: t(`platform.${platform as ChannelPlatformName}`) }),
        6,
      );
      void queryClient.invalidateQueries({ queryKey: ["channels"] });
      close();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  return (
    <Modal
      open={open}
      title={t("channels.key.title")}
      okText={mode === "bind" ? t("channels.key.send") : t("onboarding.marketplace.connect")}
      cancelText={t("common.cancel")}
      confirmLoading={submit.isPending}
      onOk={() => form.submit()}
      onCancel={close}
      destroyOnHidden
      width={520}
    >
      <Segmented
        block
        value={mode}
        onChange={(v) => setMode(v as "bind" | "key")}
        options={[
          { value: "bind", label: t("channels.key.modeBind") },
          { value: "key", label: t("channels.key.modeKey") },
        ]}
        style={{ marginBottom: 16 }}
      />
      <Typography.Paragraph type="secondary">{mode === "bind" ? t("channels.key.bindHelp") : t("channels.key.keyHelp")}</Typography.Paragraph>
      {mode === "key" && bindId && (
        <Typography.Paragraph>
          {t("channels.key.clientId")}: <Typography.Text code copyable>{bindId}</Typography.Text>
        </Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => submit.mutate(v)}>
        <Form.Item name="storeCode" label={t("channels.key.storeCode")} extra={t("channels.key.storeCodeHint")} rules={[{ required: true, message: t("field.required") }]}>
          <Input maxLength={20} autoFocus style={{ textTransform: "uppercase" }} />
        </Form.Item>
        <Form.Item name="username" label={t("channels.key.username")} rules={[{ required: true, type: "email", message: t("field.invalid_email") }]}>
          <Input type="email" maxLength={254} autoComplete="off" />
        </Form.Item>
        {mode === "key" && (
          <>
            <Form.Item name="sellerKey" label={t("channels.key.sellerKey")} rules={[{ required: true, message: t("field.required") }]}>
              <Input.Password maxLength={256} autoComplete="off" />
            </Form.Item>
            <Form.Item name="signatureKey" label={t("channels.key.signatureKey")} extra={t("channels.key.signatureHint")}>
              <Input.Password maxLength={256} autoComplete="off" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
